import React, { useRef, useEffect } from 'react'
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Globe, User, LogOut, Menu, X, Sun, Moon, Phone, Calendar, Users, Building2, Bell, BellRing, Loader2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet'
import { Separator } from '../ui/separator'
import { useAuthStore } from '../../store/authStore'
import { useTheme } from '../ui/theme-provider'
import { cn } from '../../lib/utils'
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar'
import NotificationDropdown from '../ui/NotificationDropdown'
import { useNotifications } from '../../hooks/useNotifications'
import { Badge } from '../ui/badge'
import Logo from '../ui/Logo'
import { 
  isCarDetailPage, 
  isLocationCarDetailPage, 
  translateCarSlug, 
  translateLocationCarSlug 
} from '../../utils/languageSlugTranslation'
import { extractCarDetailFromUrl } from '../../utils/seoUrls'

const Header: React.FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { lang } = useParams<{ lang: string }>()
  const { user, profile, signOut } = useAuthStore()
  const { theme, actualTheme, setTheme } = useTheme()
  const { unreadCount } = useNotifications()
  const [isMenuOpen, setIsMenuOpen] = React.useState(false)
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = React.useState(false)
  const [isMobileNotificationOpen, setIsMobileNotificationOpen] = React.useState(false)
  const [isSigningOut, setIsSigningOut] = React.useState(false)
  const languageDropdownRef = useRef<HTMLDivElement>(null)

  // Close mobile notification dropdown when main menu closes
  React.useEffect(() => {
    if (!isMenuOpen) {
      setIsMobileNotificationOpen(false)
      setIsLanguageMenuOpen(false)
    }
  }, [isMenuOpen])

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setIsLanguageMenuOpen(false)
      }
    }

    if (isLanguageMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isLanguageMenuOpen])

  // Validate language parameter - only allow valid language codes
  const validLanguages = ['en', 'fr', 'ar']
  const currentLang = lang && validLanguages.includes(lang) ? lang : 'en'

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'ar', name: 'العربية' },
  ]

  // Check if a menu item is active
  const isActiveRoute = (path: string) => {
    const currentPath = location.pathname
    const expectedPath = `/${currentLang}${path === '/' ? '' : path}`
    
    // Exact match for home page
    if (path === '/') {
      return currentPath === expectedPath || currentPath === `/${currentLang}`
    }
    
    // For other paths, check if current path starts with the expected path
    // This handles nested routes like /cars/car-details, /agencies/agency-details, etc.
    return currentPath === expectedPath || currentPath.startsWith(expectedPath + '/')
  }

  const handleLanguageChange = (languageCode: string) => {
    console.log('Header handleLanguageChange:', {
      from: currentLang,
      to: languageCode,
      currentPath: window.location.pathname
    })
    
    // Save language preference to localStorage
    localStorage.setItem('kiracar-language', languageCode)
    
    const currentPath = window.location.pathname
    let newPath = currentPath
    
    // Check if this is a car detail page that needs URL translation
    if (isCarDetailPage(currentPath)) {
      if (isLocationCarDetailPage(currentPath)) {
        // Handle location-based car detail pages (e.g., /fr/location-voiture/marrakech/car-slug)
        const urlInfo = extractCarDetailFromUrl(currentPath)
        if (urlInfo.carSlug && urlInfo.locationSlug) {
          // Translate the car slug
          const translatedSlug = translateLocationCarSlug(urlInfo.carSlug, currentLang, languageCode)
          
          // Generate new URL with proper language prefix
          const prefixes = {
            fr: 'location-voiture',
            en: 'rent-car',
            ar: 'تأجير-سيارة'
          }
          const newPrefix = prefixes[languageCode as keyof typeof prefixes] || prefixes.fr
          
          newPath = `/${languageCode}/${newPrefix}/${urlInfo.locationSlug}/${translatedSlug}`
        } else {
          // Fallback to simple language prefix replacement
          const pathWithoutLang = currentPath.replace(/^\/[a-z]{2}(\/|$)/, '/')
          newPath = `/${languageCode}${pathWithoutLang === '/' ? '' : pathWithoutLang}`
        }
      } else {
        // Handle regular car detail pages (e.g., /fr/cars/location-voiture-car-slug)
        const pathParts = currentPath.split('/')
        if (pathParts.length >= 4 && pathParts[2] === 'cars') {
          const currentSlug = pathParts[3]
          const translatedSlug = translateCarSlug(currentSlug, currentLang, languageCode)
          newPath = `/${languageCode}/cars/${translatedSlug}`
        } else {
          // Fallback to simple language prefix replacement
          const pathWithoutLang = currentPath.replace(/^\/[a-z]{2}(\/|$)/, '/')
          newPath = `/${languageCode}${pathWithoutLang === '/' ? '' : pathWithoutLang}`
        }
      }
    } else {
      // For non-car pages, just replace the language prefix
      const pathWithoutLang = currentPath.replace(/^\/[a-z]{2}(\/|$)/, '/')
      newPath = `/${languageCode}${pathWithoutLang === '/' ? '' : pathWithoutLang}`
    }
    
    console.log('Header navigating to:', newPath)
    navigate(newPath)
    setIsLanguageMenuOpen(false)
  }

  const handleSignOut = async () => {
    setIsSigningOut(true)
    try {
      await signOut()
      navigate(`/${currentLang}`)
    } catch (error) {
      console.error('Error signing out:', error)
    } finally {
      setIsSigningOut(false)
    }
  }

  const getDashboardPath = () => {
    if (!profile) return `/${currentLang}/dashboard`
    switch (profile.role) {
      case 'admin':
        return `/${currentLang}/admin`
      case 'agency':
        return `/${currentLang}/agency/dashboard`
      default:
        return `/${currentLang}/dashboard`
    }
  }

  const toggleTheme = () => {
    // Simple toggle between light and dark (no system mode cycling)
    if (actualTheme === 'light') {
      setTheme('dark')
    } else {
      setTheme('light')
    }
  }

  const getThemeIcon = () => {
    // Use actualTheme for consistent icon display
    if (actualTheme === 'light') return <Sun className="h-4 w-4" />
    return <Moon className="h-4 w-4" />
  }

  const getThemeTooltip = () => {
    if (actualTheme === 'light') return 'Switch to dark mode'
    return 'Switch to light mode'
  }

  // Get current date formatted
  const getCurrentDate = () => {
    const today = new Date()
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }
    return today.toLocaleDateString(currentLang === 'ar' ? 'ar-MA' : currentLang === 'fr' ? 'fr-FR' : 'en-US', options)
  }

  return (
    <header className="w-full bg-gradient-to-b from-gray-50 via-brand-50/80 to-gray-50 dark:from-gray-900 dark:via-brand-950/30 dark:to-background">

      {/* Main Header */}
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Logo size="lg" />

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 rtl:space-x-reverse">
            <Link
              to={`/${currentLang}`}
              className={cn(
                "font-semibold transition-all duration-300 uppercase tracking-wide px-3 py-2 rounded-lg relative group",
                currentLang === 'ar' ? "font-zain text-base" : "font-poppins text-sm",
                isActiveRoute('/') 
                  ? "bg-brand-500 text-black" 
                  : "text-foreground hover:text-brand-600 dark:hover:text-brand-400 hover:bg-brand-50/80 dark:hover:bg-brand-900/40 hover:shadow-sm"
              )}
            >
              {t('home')}
              {!isActiveRoute('/') && (
                <span className="absolute inset-x-0 bottom-0 h-0.5 bg-brand-500 dark:bg-brand-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-full" />
              )}
            </Link>
            <Link
              to={`/${currentLang}/cars`}
              className={cn(
                "font-semibold transition-all duration-300 uppercase tracking-wide px-3 py-2 rounded-lg relative group",
                currentLang === 'ar' ? "font-zain text-base" : "font-poppins text-sm",
                isActiveRoute('/cars') 
                  ? "bg-brand-500 text-black" 
                  : "text-foreground hover:text-brand-600 dark:hover:text-brand-400 hover:bg-brand-50/80 dark:hover:bg-brand-900/40 hover:shadow-sm"
              )}
            >
              {t('common:cars')}
              {!isActiveRoute('/cars') && (
                <span className="absolute inset-x-0 bottom-0 h-0.5 bg-brand-500 dark:bg-brand-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-full" />
              )}
            </Link>
            <Link
              to={`/${currentLang}/agencies`}
              className={cn(
                "font-semibold transition-all duration-300 uppercase tracking-wide px-3 py-2 rounded-lg relative group",
                currentLang === 'ar' ? "font-zain text-base" : "font-poppins text-sm",
                isActiveRoute('/agencies') 
                  ? "bg-brand-500 text-black" 
                  : "text-foreground hover:text-brand-600 dark:hover:text-brand-400 hover:bg-brand-50/80 dark:hover:bg-brand-900/40 hover:shadow-sm"
              )}
            >
              {t('agencies')}
              {!isActiveRoute('/agencies') && (
                <span className="absolute inset-x-0 bottom-0 h-0.5 bg-brand-500 dark:bg-brand-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-full" />
              )}
            </Link>
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4 rtl:space-x-reverse">
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="flex items-center space-x-2 rtl:space-x-reverse font-medium hover:text-brand-600 dark:hover:text-brand-400 bg-brand-500/20 hover:bg-brand-500/30 dark:bg-brand-900/20 dark:hover:bg-brand-950/30 transition-all duration-300 rounded-lg p-2"
              title={getThemeTooltip()}
            >
              {getThemeIcon()}
            </Button>

            {/* Notifications - Only for agency and admin users */}
            {user && profile && (profile.role === 'agency' || profile.role === 'admin') && (
              <NotificationDropdown />
            )}

            {/* Language Switcher */}
            <div className="relative" ref={languageDropdownRef}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                className="flex items-center space-x-2 rtl:space-x-reverse font-medium hover:text-brand-600 dark:hover:text-brand-400 bg-brand-500/20 hover:bg-brand-500/30 dark:bg-brand-900/20 dark:hover:bg-brand-950/30 transition-all duration-300 rounded-lg px-3 py-2"
              >
                <Globe className="h-4 w-4" />
                <span className="text-sm font-semibold">
                  {currentLang.toUpperCase()}
                </span>
              </Button>

              <AnimatePresence>
                {isLanguageMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 rtl:right-auto rtl:left-0 mt-2 w-48 rounded-xl border border-border/50 bg-card/95 backdrop-blur-md p-2 shadow-xl z-50"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={cn(
                          "flex w-full items-center space-x-3 rtl:space-x-reverse rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-300 hover:bg-brand-50/80 dark:hover:bg-brand-900/40 hover:text-brand-600 dark:hover:text-brand-400 text-foreground hover:shadow-sm",
                          currentLang === lang.code && "bg-brand-500 text-black"
                        )}
                      >
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Auth Actions */}
            {user ? (
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                {/* User Profile Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={profile?.avatar_url || ''} alt={profile?.full_name || 'User'} />
                        <AvatarFallback>{profile?.full_name?.charAt(0) || 'U'}</AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end" forceMount>
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">
                          {profile?.full_name || t('common.user')}
                        </p>
                        <p className="text-xs leading-none text-muted-foreground">
                          {user?.email}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => navigate(getDashboardPath())}
                      className="cursor-pointer"
                    >
                      <User className="mr-2 h-4 w-4" />
                      <span>{t('common.dashboard', 'Dashboard')}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleSignOut}
                      disabled={isSigningOut}
                      className="cursor-pointer"
                    >
                      {isSigningOut ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          <span>{t('auth.signingOut', 'Signing out...')}</span>
                        </>
                      ) : (
                        <>
                          <LogOut className="mr-2 h-4 w-4" />
                          <span>{t('logout')}</span>
                        </>
                      )}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/${currentLang}/login`)}
                  className="font-medium bg-black border-brand-500 text-white hover:text-brand-600 hover:bg-black dark:text-white dark:border-black dark:hover:text-brand-300 dark:hover:bg-brand-900/40 transition-all duration-300 rounded-lg px-4 py-2 shadow-sm hover:shadow-md"
                >
                  {t('login')}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => navigate(`/${currentLang}/signup`)}
                  className="font-medium px-4 py-2 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  {t('signup')}
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>
        </div>

        {/* Material 3 Mobile Sidebar */}
        <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <SheetContent 
            side={currentLang === 'ar' ? 'right' : 'left'}
            className={cn(
              "w-[280px] sm:w-[320px] p-0 bg-background/95 backdrop-blur-xl dark:border-border/50 shadow-2xl",
              currentLang === 'ar' 
                ? "border-l rounded-l-3xl rounded-r-none" 
                : "border-r rounded-r-3xl rounded-l-none"
            )}
          >
            <div className="flex flex-col h-full">
              {/* Header Section */}
              <SheetHeader className="px-6 pt-6 pb-4 border-b border-border/50">
                <div className="flex items-center justify-between rtl:flex-row-reverse">
                  <SheetTitle className={cn(
                    "text-xl font-bold text-foreground",
                    currentLang === 'ar' ? "text-right" : "text-left"
                  )}>
                    {t('common.menu', 'Menu')}
                  </SheetTitle>
                </div>
              </SheetHeader>

              {/* Navigation Section */}
              <div className={cn(
                "flex-1 overflow-y-auto px-3 py-4",
                currentLang === 'ar' && "rtl"
              )}>
                <nav className="space-y-1">
                  <Link
                    to={`/${currentLang}`}
                    onClick={() => setIsMenuOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 rounded-full text-base font-medium transition-all duration-200",
                      currentLang === 'ar' ? "font-zain flex-row-reverse" : "font-poppins",
                      isActiveRoute('/')
                        ? "bg-brand-500 text-black shadow-md"
                        : "text-foreground hover:bg-muted/80 active:bg-muted"
                    )}
                  >
                    <span className="text-lg">{t('home')}</span>
                  </Link>
                  <Link
                    to={`/${currentLang}/cars`}
                    onClick={() => setIsMenuOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 rounded-full text-base font-medium transition-all duration-200",
                      currentLang === 'ar' ? "font-zain flex-row-reverse" : "font-poppins",
                      isActiveRoute('/cars')
                        ? "bg-brand-500 text-black shadow-md"
                        : "text-foreground hover:bg-muted/80 active:bg-muted"
                    )}
                  >
                    <span className="text-lg">{t('common:cars')}</span>
                  </Link>
                  <Link
                    to={`/${currentLang}/agencies`}
                    onClick={() => setIsMenuOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 rounded-full text-base font-medium transition-all duration-200",
                      currentLang === 'ar' ? "font-zain flex-row-reverse" : "font-poppins",
                      isActiveRoute('/agencies')
                        ? "bg-brand-500 text-black shadow-md"
                        : "text-foreground hover:bg-muted/80 active:bg-muted"
                    )}
                  >
                    <span className="text-lg">{t('agencies')}</span>
                  </Link>
                </nav>

                <Separator className="my-4" />

                {/* Settings Section */}
                <div className="space-y-1">
                  <Button
                    variant="ghost"
                    size="lg"
                    onClick={toggleTheme}
                    className={cn(
                      "w-full gap-3 px-4 py-3 rounded-full text-base font-medium hover:bg-muted/80 active:bg-muted",
                      currentLang === 'ar' ? "justify-end flex-row-reverse" : "justify-start"
                    )}
                  >
                    {getThemeIcon()}
                    <span>{actualTheme === 'light' ? t('common.darkMode', 'Dark Mode') : t('common.lightMode', 'Light Mode')}</span>
                  </Button>
                  
                  <div className="relative">
                    <Button
                      variant="ghost"
                      size="lg"
                      onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                      className={cn(
                        "w-full gap-3 px-4 py-3 rounded-full text-base font-medium hover:bg-muted/80 active:bg-muted",
                        currentLang === 'ar' ? "justify-end flex-row-reverse" : "justify-start"
                      )}
                    >
                      <Globe className="h-5 w-5" />
                      <span>{languages.find(lang => lang.code === currentLang)?.name || t('common.language', 'Language')}</span>
                    </Button>

                    <AnimatePresence>
                      {isLanguageMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                          className="absolute left-0 right-0 mt-2 rounded-2xl border border-border/50 bg-card/95 backdrop-blur-md p-2 shadow-xl z-50"
                        >
                          {languages.map((lang) => (
                            <button
                              key={lang.code}
                              onClick={() => {
                                handleLanguageChange(lang.code)
                                setIsLanguageMenuOpen(false)
                                setIsMenuOpen(false)
                              }}
                              className={cn(
                                "flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200 hover:bg-brand-50/80 dark:hover:bg-brand-900/40 text-foreground",
                                currentLang === lang.code && "bg-brand-500 text-black"
                              )}
                            >
                              <span>{lang.name}</span>
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  
                  {/* Mobile Notifications */}
                  {user && (
                    <Button
                      variant="ghost"
                      size="lg"
                      onClick={() => setIsMobileNotificationOpen(!isMobileNotificationOpen)}
                      className={cn(
                        "w-full gap-3 px-4 py-3 rounded-full text-base font-medium hover:bg-muted/80 active:bg-muted relative",
                        currentLang === 'ar' ? "justify-end flex-row-reverse" : "justify-start"
                      )}
                    >
                      {unreadCount > 0 ? (
                        <BellRing className="h-5 w-5" />
                      ) : (
                        <Bell className="h-5 w-5" />
                      )}
                      <span>{t('common.notifications.title') || 'Notifications'}</span>
                      {unreadCount > 0 && (
                        <Badge 
                          variant="destructive" 
                          className={cn(
                            "h-6 min-w-6 flex items-center justify-center px-1.5 text-xs rounded-full",
                            currentLang === 'ar' ? "mr-auto" : "ml-auto"
                          )}
                        >
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </Badge>
                      )}
                    </Button>
                  )}
                </div>

                {isMobileNotificationOpen && user && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mt-2 rounded-2xl border border-border/50 bg-card/95 backdrop-blur-md shadow-xl p-2"
                  >
                    <NotificationDropdown />
                  </motion.div>
                )}
              </div>

              {/* Footer Section */}
              <div className="border-t border-border/50 px-6 py-4 space-y-2">
                {user ? (
                  <>
                    <Button
                      variant="ghost"
                      size="lg"
                      onClick={() => {
                        navigate(getDashboardPath())
                        setIsMenuOpen(false)
                      }}
                      className={cn(
                        "w-full gap-3 px-4 py-3 rounded-full text-base font-medium hover:bg-muted/80 active:bg-muted",
                        currentLang === 'ar' ? "justify-end flex-row-reverse" : "justify-start"
                      )}
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={profile?.avatar_url || ''} alt={profile?.full_name || user.email} />
                        <AvatarFallback className="text-xs">
                          {(profile?.full_name || user.email)?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className={cn(
                        "flex flex-col",
                        currentLang === 'ar' ? "items-end" : "items-start"
                      )}>
                        <span className="text-sm font-medium">{profile?.full_name || t('common.user')}</span>
                        <span className="text-xs text-muted-foreground">{t('common.dashboard', 'Dashboard')}</span>
                      </div>
                    </Button>
                    <Button
                      variant="ghost"
                      size="lg"
                      onClick={() => {
                        handleSignOut()
                        setIsMenuOpen(false)
                      }}
                      disabled={isSigningOut}
                      className={cn(
                        "w-full gap-3 px-4 py-3 rounded-full text-base font-medium text-destructive hover:bg-destructive/10 active:bg-destructive/20",
                        currentLang === 'ar' ? "justify-end flex-row-reverse" : "justify-start"
                      )}
                    >
                      {isSigningOut ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          <span>{t('auth.signingOut', 'Signing out...')}</span>
                        </>
                      ) : (
                        <>
                          <LogOut className="h-5 w-5" />
                          <span>{t('logout')}</span>
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => {
                        navigate(`/${currentLang}/login`)
                        setIsMenuOpen(false)
                      }}
                      className="w-full rounded-full font-medium border-brand-500 text-brand-600 hover:text-brand-700 hover:bg-brand-50 dark:text-brand-400 dark:border-brand-400 dark:hover:text-brand-300 dark:hover:bg-brand-900/40"
                    >
                      {t('login')}
                    </Button>
                    <Button
                      variant="default"
                      size="lg"
                      onClick={() => {
                        navigate(`/${currentLang}/signup`)
                        setIsMenuOpen(false)
                      }}
                      className="w-full rounded-full font-medium shadow-md"
                    >
                      {t('signup')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  )
}

export default Header 